import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Tag,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Settings,
  ArrowLeft,
  RefreshCw,
  Loader2
} from 'lucide-react'
import { useNotification } from '../../contexts/NotificationContext'

interface CustomField {
  id: string
  category_id: string
  name: string  // Changed from field_name to match API response
  field_type: 'text' | 'number' | 'date' | 'boolean' | 'select'
  required: boolean
  sort_order: number
  options?: string[]  // For select type
  created_at: string
}

interface Category {
  id: string
  name: string
  description: string
  custom_fields?: CustomField[]
}

const API_BASE = ''

export default function CustomFieldsPage() {
  const navigate = useNavigate()
  const { success, error: showError } = useNotification()
  const [categories, setCategories] = useState<Category[]>([])
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingField, setEditingField] = useState<CustomField | null>(null)
  const [formData, setFormData] = useState({
    field_name: '',
    field_type: 'text' as CustomField['field_type'],
    required: false,
    sort_order: 0,
    options: '' // Comma-separated string for UI
  })
  const [loading, setLoading] = useState(false)
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [categoryFieldCounts, setCategoryFieldCounts] = useState<Record<string, number>>({})

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories()
  }, [])

  // Fetch fields when category selected
  useEffect(() => {
    if (selectedCategory) {
      fetchFieldsForCategory(selectedCategory.id)
    }
  }, [selectedCategory?.id])

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true)
      const response = await fetch(`${API_BASE}/api/categories-pg`)
      const data = await response.json()

      if (data.success) {
        const cats = data.data || []
        setCategories(cats)

        // Fetch field counts for each category
        const counts: Record<string, number> = {}
        await Promise.all(
          cats.map(async (cat: Category) => {
            try {
              const fieldsResponse = await fetch(`${API_BASE}/api/category-fields-pg?category_id=${cat.id}`)
              const fieldsData = await fieldsResponse.json()
              console.log(`Field count for ${cat.name}:`, fieldsData)
              counts[cat.id] = fieldsData.success ? (fieldsData.data?.length || 0) : 0
            } catch (err) {
              console.error(`Error fetching fields for ${cat.name}:`, err)
              counts[cat.id] = 0
            }
          })
        )
        console.log('Final counts:', counts)
        setCategoryFieldCounts(counts)
      } else {
        showError('Failed to load categories')
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      showError('Failed to load categories')
    } finally {
      setLoadingCategories(false)
    }
  }

  const fetchFieldsForCategory = async (categoryId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/category-fields-pg?category_id=${categoryId}`)
      const data = await response.json()

      if (data.success) {
        setSelectedCategory(prev => prev ? { ...prev, custom_fields: data.data } : null)
      }
    } catch (error) {
      console.error('Error fetching category fields:', error)
      showError('Failed to load category fields')
    }
  }

  const handleCreateField = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.field_name.trim()) {
      showError('Field name is required')
      return
    }

    if (!selectedCategory) {
      showError('Please select a category')
      return
    }

    // Validate select field has options
    if (formData.field_type === 'select' && !formData.options.trim()) {
      showError('Select fields require at least one option')
      return
    }

    setLoading(true)

    try {
      // Parse options from comma-separated string
      const optionsArray = formData.field_type === 'select'
        ? formData.options.split(',').map(opt => opt.trim()).filter(opt => opt.length > 0)
        : []

      const response = await fetch(`${API_BASE}/api/category-fields-pg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category_id: selectedCategory.id,
          name: formData.field_name,  // API expects 'name' not 'field_name'
          field_type: formData.field_type,
          required: formData.required,
          options: optionsArray
        })
      })

      const data = await response.json()

      if (data.success) {
        success('Field created successfully')
        await fetchFieldsForCategory(selectedCategory.id)
        setFormData({ field_name: '', field_type: 'text', required: false, sort_order: 0, options: '' })
        setShowCreateDialog(false)
      } else {
        showError(data.error || 'Failed to create field')
      }
    } catch (error) {
      console.error('Error creating field:', error)
      showError('Failed to create field')
    } finally {
      setLoading(false)
    }
  }

  const handleEditField = (field: CustomField) => {
    setEditingField(field)
    setFormData({
      field_name: field.name,  // API returns 'name', we store in 'field_name' for form
      field_type: field.field_type,
      required: field.required,
      sort_order: field.sort_order,
      options: field.options ? field.options.join(', ') : ''
    })
  }

  const handleUpdateField = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!editingField || !selectedCategory) return

    // Validate select field has options
    if (formData.field_type === 'select' && !formData.options.trim()) {
      showError('Select fields require at least one option')
      return
    }

    setLoading(true)

    try {
      // Parse options from comma-separated string
      const optionsArray = formData.field_type === 'select'
        ? formData.options.split(',').map(opt => opt.trim()).filter(opt => opt.length > 0)
        : []

      const response = await fetch(`${API_BASE}/api/category-fields-pg/${editingField.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.field_name,  // API expects 'name' not 'field_name'
          field_type: formData.field_type,
          required: formData.required,
          options: optionsArray
        })
      })

      const data = await response.json()

      if (data.success) {
        success('Field updated successfully')
        await fetchFieldsForCategory(selectedCategory.id)
        setEditingField(null)
        setFormData({ field_name: '', field_type: 'text', required: false, sort_order: 0, options: '' })
      } else {
        showError(data.error || 'Failed to update field')
      }
    } catch (error) {
      console.error('Error updating field:', error)
      showError('Failed to update field')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteField = async (fieldId: string) => {
    if (!selectedCategory) return
    if (!confirm('Are you sure you want to delete this field?')) return

    setLoading(true)

    try {
      const response = await fetch(`${API_BASE}/api/category-fields-pg?id=${fieldId}`, {
        method: 'DELETE'
      })

      const data = await response.json()

      if (data.success) {
        success('Field deleted successfully')
        await fetchFieldsForCategory(selectedCategory.id)
      } else {
        showError(data.error || 'Failed to delete field')
      }
    } catch (error) {
      console.error('Error deleting field:', error)
      showError('Failed to delete field')
    } finally {
      setLoading(false)
    }
  }

  const getTypeIcon = (type: CustomField['field_type']) => {
    const icons = {
      text: '📝',
      number: '🔢',
      date: '📅',
      boolean: '✅',
      select: '📋'
    }
    return icons[type]
  }

  const getTypeColor = (type: CustomField['field_type']) => {
    const colors = {
      text: '#00F0FF',
      number: '#6BCF7F',
      date: '#B91BFF',
      boolean: '#FF6B35',
      select: '#FF1493'
    }
    return colors[type]
  }

  if (loadingCategories) {
    return (
      <div style={{
        padding: '24px',
        background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 style={{ width: 48, height: 48, color: '#FF6B35', animation: 'spin 1s linear infinite' }} />
          <p style={{ color: '#8A95AA', marginTop: '16px' }}>Loading categories...</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '48px',
              fontWeight: 700,
              color: '#FF6B35',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: 0,
              textShadow: '0 0 20px rgba(255, 107, 53, 0.6)'
            }}>
              CUSTOM FIELDS MANAGEMENT
            </h1>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              color: '#8A95AA',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: '8px 0 0 0'
            }}>
              CONFIGURE CUSTOM FIELDS FOR CATEGORIES
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                background: 'rgba(138, 149, 170, 0.1)',
                border: '2px solid rgba(138, 149, 170, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#8A95AA',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
            >
              <ArrowLeft style={{ width: 16, height: 16 }} />
              BACK
            </button>
          </div>
        </div>
      </div>

      {/* Category Selection */}
      <div style={{
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(8px)',
        border: '2px solid rgba(255, 20, 147, 0.3)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 0 30px rgba(255, 20, 147, 0.15)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <Tag style={{ width: 24, height: 24, color: '#FF1493' }} />
          <h3 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#FF1493',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            margin: 0
          }}>
            SELECT CATEGORY
          </h3>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '16px'
        }}>
          {categories.map((category) => (
            <div
              key={category.id}
              onClick={() => setSelectedCategory(category)}
              style={{
                background: selectedCategory?.id === category.id
                  ? 'linear-gradient(135deg, rgba(255, 20, 147, 0.15), rgba(185, 27, 255, 0.15))'
                  : 'linear-gradient(135deg, rgba(26, 31, 46, 0.6), rgba(36, 41, 56, 0.6))',
                border: selectedCategory?.id === category.id
                  ? '2px solid #FF1493'
                  : '2px solid rgba(255, 20, 147, 0.3)',
                borderRadius: '8px',
                padding: '16px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                boxShadow: selectedCategory?.id === category.id
                  ? '0 0 25px rgba(255, 20, 147, 0.3)'
                  : 'none'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <h4 style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#FF1493',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  margin: 0
                }}>
                  {category.name}
                </h4>
                <span style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: categoryFieldCounts[category.id] > 0 ? '#6BCF7F' : '#8A95AA',
                  background: categoryFieldCounts[category.id] > 0 ? 'rgba(107, 207, 127, 0.15)' : 'rgba(138, 149, 170, 0.15)',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  border: `1px solid ${categoryFieldCounts[category.id] > 0 ? 'rgba(107, 207, 127, 0.3)' : 'rgba(138, 149, 170, 0.3)'}`,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {categoryFieldCounts[category.id] || 0} {categoryFieldCounts[category.id] === 1 ? 'field' : 'fields'}
                </span>
              </div>
              <p style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '13px',
                color: '#8A95AA',
                margin: 0
              }}>
                {category.description || 'No description'}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Fields List */}
      {selectedCategory && (
        <div style={{
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          border: '2px solid rgba(255, 20, 147, 0.3)',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 0 30px rgba(255, 20, 147, 0.15)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Settings style={{ width: 24, height: 24, color: '#B91BFF' }} />
              <h3 style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '24px',
                fontWeight: 700,
                color: '#B91BFF',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                margin: 0
              }}>
                CUSTOM FIELDS FOR {selectedCategory.name.toUpperCase()}
              </h3>
            </div>
            <button
              onClick={() => setShowCreateDialog(true)}
              style={{
                background: 'rgba(185, 27, 255, 0.2)',
                border: '2px solid #B91BFF',
                borderRadius: '6px',
                padding: '10px 16px',
                color: '#B91BFF',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
            >
              <Plus style={{ width: 14, height: 14 }} />
              ADD FIELD
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {selectedCategory.custom_fields?.map((field) => (
              <div
                key={field.id}
                style={{
                  background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.8), rgba(36, 41, 56, 0.8))',
                  border: '2px solid rgba(255, 20, 147, 0.3)',
                  borderRadius: '8px',
                  padding: '16px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '6px',
                      background: `${getTypeColor(field.field_type)}20`,
                      border: `2px solid ${getTypeColor(field.field_type)}50`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px'
                    }}>
                      {getTypeIcon(field.field_type)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '18px',
                          fontWeight: 700,
                          color: '#FF1493',
                          textTransform: 'uppercase',
                          letterSpacing: '1px'
                        }}>
                          {field.name}
                        </span>
                        {field.required && (
                          <div style={{
                            background: 'rgba(239, 68, 68, 0.2)',
                            border: '1px solid #EF4444',
                            borderRadius: '4px',
                            padding: '2px 8px',
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '10px',
                            fontWeight: 700,
                            color: '#EF4444',
                            letterSpacing: '1px'
                          }}>
                            REQUIRED
                          </div>
                        )}
                        <div style={{
                          background: 'rgba(255, 20, 147, 0.2)',
                          border: '1px solid rgba(255, 20, 147, 0.4)',
                          borderRadius: '4px',
                          padding: '2px 8px',
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '10px',
                          fontWeight: 700,
                          color: '#FF1493',
                          letterSpacing: '1px',
                          textTransform: 'uppercase'
                        }}>
                          {field.field_type}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleEditField(field)}
                      style={{
                        background: 'rgba(255, 217, 61, 0.1)',
                        border: '2px solid rgba(255, 217, 61, 0.4)',
                        borderRadius: '6px',
                        padding: '8px',
                        color: '#FFD93D',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Edit style={{ width: 16, height: 16 }} />
                    </button>
                    <button
                      onClick={() => handleDeleteField(field.id)}
                      style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '2px solid rgba(239, 68, 68, 0.4)',
                        borderRadius: '6px',
                        padding: '8px',
                        color: '#EF4444',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Trash2 style={{ width: 16, height: 16 }} />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {(!selectedCategory.custom_fields || selectedCategory.custom_fields.length === 0) && (
              <div style={{
                textAlign: 'center',
                padding: '48px 24px'
              }}>
                <Settings style={{ width: 48, height: 48, color: '#FF1493', opacity: 0.5, marginBottom: '16px' }} />
                <p style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#E0E6F0',
                  margin: '0 0 8px 0',
                  letterSpacing: '1px'
                }}>
                  NO CUSTOM FIELDS CONFIGURED
                </p>
                <p style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '14px',
                  color: '#8A95AA',
                  margin: 0
                }}>
                  Click "Add Field" to create your first custom field
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Field Dialog */}
      {(showCreateDialog || editingField) && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.95), rgba(36, 41, 56, 0.95))',
            backdropFilter: 'blur(12px)',
            border: '2px solid rgba(255, 20, 147, 0.4)',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '20px',
                fontWeight: 700,
                color: '#FF1493',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                margin: 0
              }}>
                {editingField ? 'EDIT FIELD' : 'CREATE CUSTOM FIELD'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateDialog(false)
                  setEditingField(null)
                  setFormData({ field_name: '', field_type: 'text', required: false, sort_order: 0, options: '' })
                }}
                style={{
                  background: 'rgba(138, 149, 170, 0.1)',
                  border: '2px solid rgba(138, 149, 170, 0.4)',
                  borderRadius: '6px',
                  padding: '8px',
                  color: '#8A95AA',
                  cursor: 'pointer'
                }}
              >
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>

            <form onSubmit={editingField ? handleUpdateField : handleCreateField} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '11px',
                  color: '#8A95AA',
                  marginBottom: '6px',
                  display: 'block',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>FIELD NAME *</label>
                <input
                  type="text"
                  value={formData.field_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, field_name: e.target.value }))}
                  placeholder="Enter field name"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '11px',
                  color: '#8A95AA',
                  marginBottom: '6px',
                  display: 'block',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>FIELD TYPE *</label>
                <select
                  value={formData.field_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, field_type: e.target.value as CustomField['field_type'] }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="boolean">Boolean</option>
                  <option value="select">Select</option>
                </select>
              </div>

              {/* Options - Only show for select type */}
              {formData.field_type === 'select' && (
                <div>
                  <label style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '11px',
                    color: '#8A95AA',
                    marginBottom: '6px',
                    display: 'block',
                    letterSpacing: '1px',
                    textTransform: 'uppercase'
                  }}>SELECT OPTIONS * (COMMA-SEPARATED)</label>
                  <input
                    type="text"
                    value={formData.options}
                    onChange={(e) => setFormData(prev => ({ ...prev, options: e.target.value }))}
                    placeholder="e.g., Small, Medium, Large, X-Large"
                    required={formData.field_type === 'select'}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '2px solid rgba(255, 20, 147, 0.3)',
                      borderRadius: '6px',
                      color: '#FFFFFF',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  <p style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '11px',
                    color: '#8A95AA',
                    marginTop: '6px',
                    marginBottom: 0
                  }}>
                    Enter options separated by commas
                  </p>
                </div>
              )}

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                color: '#E0E6F0',
                fontWeight: 600
              }}>
                <input
                  type="checkbox"
                  checked={formData.required}
                  onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#FF1493'
                  }}
                />
                REQUIRED FIELD
              </label>

              <div style={{
                display: 'flex',
                gap: '12px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(255, 20, 147, 0.2)'
              }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1,
                    background: 'rgba(107, 207, 127, 0.2)',
                    border: '2px solid #6BCF7F',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    color: '#6BCF7F',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.5 : 1
                  }}
                >
                  {loading ? 'SAVING...' : (editingField ? 'UPDATE FIELD' : 'CREATE FIELD')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateDialog(false)
                    setEditingField(null)
                    setFormData({ field_name: '', field_type: 'text', required: false, sort_order: 0 })
                  }}
                  style={{
                    background: 'rgba(138, 149, 170, 0.1)',
                    border: '2px solid rgba(138, 149, 170, 0.4)',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    color: '#8A95AA',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: 'pointer'
                  }}
                >
                  CANCEL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
